import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { CSSProperties, useState } from 'react'

import ModalActionContainer from 'components/containers/modalActionContainer'
import KanseiAutoComplete from 'components/copied/autoComplete'
import { helper } from 'helper'
import { Option } from 'types/option'
import { PrimaryWorkPatternContent } from 'types/primaryWorkPattern'
import { Security } from 'types/security'

interface Props {
  workPatternOptions: Option[]
  securityOptions: Option[]
  onCancel: () => void
  onAdd: (data: {
    securityId: string
    primaryWorks: PrimaryWorkPatternContent[]
  }) => void
  onUpdate: (
    id: string,
    data: {
      primaryWorks: PrimaryWorkPatternContent[]
    },
  ) => void
  defaultValue?: {
    id: string
    security: Security
    relationIds: Set<string> // workPatternIds
  }
}

const Mode = {
  ADD: 0,
  EDIT: 1,
} as const
type ModeKey = (typeof Mode)[keyof typeof Mode]

export default function FixedSecuritySettingModal(props: Props) {
  const [fixedSecurityId, setFixedSecurityId] = useState<string | null>(
    props.defaultValue?.security.id ?? null,
  )

  const [relations, setRelations] = useState<Set<string>>(
    props.defaultValue?.relationIds ?? new Set(),
  )

  const mode: ModeKey = props.defaultValue == null ? Mode.ADD : Mode.EDIT

  const handleChangeFixedSecurity = (newValue: Option | null) => {
    if (newValue != null) {
      setFixedSecurityId(newValue.value as string)
    } else {
      setFixedSecurityId(null)
    }
  }

  const handleChangeRelation = (workPatternId: string) => (e: any) => {
    const newRelations = new Set(relations)
    if (e.target.checked) {
      newRelations.add(workPatternId)
    } else {
      newRelations.delete(workPatternId)
    }
    setRelations(newRelations)
  }

  const handleSave = () => {
    if (fixedSecurityId == null) {
      return
    }

    if (mode === Mode.ADD) {
      props.onAdd({
        securityId: fixedSecurityId,
        primaryWorks: Array.from(relations).map((workPatternId) => ({
          workPatternId,
        })),
      })
      return
    }

    if (mode === Mode.EDIT && props.defaultValue != null) {
      props.onUpdate(props.defaultValue.id, {
        primaryWorks: Array.from(relations).map((workPatternId) => ({
          workPatternId,
        })),
      })
      return
    }

    return
  }

  return (
    <Box>
      <Box style={formContainerStyle}>
        <Box>
          <Typography style={securityTitleStyle}>固定隊員</Typography>
          {mode === Mode.ADD && (
            <KanseiAutoComplete
              id="fixedSecurity"
              options={props.securityOptions}
              onChange={handleChangeFixedSecurity}
              fullWidth={true}
            />
          )}

          {mode === Mode.EDIT && props.defaultValue && (
            <Typography style={fixedSecurityStyle}>
              {helper.formatter.nameFormat(props.defaultValue.security)}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography style={relationTitleStyle}>優先勤務パターン</Typography>
          <Box style={relationContainerStyle}>
            {props.workPatternOptions.map((work, i) => (
              <FormControlLabel
                style={checkboxContainerStyle}
                key={`work-${i}`}
                control={
                  <Checkbox
                    style={checkboxStyle}
                    checked={relations.has(work.value as string)}
                    onChange={handleChangeRelation(work.value as string)}
                  />
                }
                label={work.label}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <ModalActionContainer>
        <Button onClick={props.onCancel} variant="negative">
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained">
          保存
        </Button>
      </ModalActionContainer>
    </Box>
  )
}
const formContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const securityTitleStyle: CSSProperties = {
  marginBottom: '8px',
}

const fixedSecurityStyle: CSSProperties = {
  fontWeight: 'bold',
}

const relationContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: '8px',
}

const relationTitleStyle: CSSProperties = {
  marginBottom: '8px',
}

const checkboxContainerStyle: CSSProperties = {
  marginRight: '16px',
}

const checkboxStyle: CSSProperties = {
  padding: 0,
}
